/**
 * Follow system — anonymous, no-login favoriting of trucks.
 *
 * Keys (all localStorage, prefixed `tt:follow:`):
 *   tt:follow:id            → stable anonymous follower UUID (created lazily)
 *   tt:follow:ids           → JSON array of followed truck_ids (mirrors server)
 *   tt:follow:email         → email if the user gave one
 *   tt:follow:opt           → "1" if notifications opted-in, else "0" / missing
 *   tt:follow:prompt:email  → follow count at which we last asked for email;
 *                             used to avoid nagging the user twice.
 */
import api from "./api";

const K_ID = "tt:follow:id";
const K_IDS = "tt:follow:ids";
const K_EMAIL = "tt:follow:email";
const K_OPT = "tt:follow:opt";
const K_PROMPTED = "tt:follow:prompt:email";

function uuid() {
  // Tiny, dependency-free. We only need collision-resistance across anon users.
  const r = () => Math.floor(Math.random() * 1e9).toString(36);
  return `${Date.now().toString(36)}-${r()}${r()}${r()}`;
}

function safeGet(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}
function safeSet(key, val) {
  try {
    if (val == null) localStorage.removeItem(key);
    else localStorage.setItem(key, String(val));
  } catch { /* private mode */ }
}

export function getFollowerId() {
  let id = safeGet(K_ID);
  if (!id) {
    id = uuid();
    safeSet(K_ID, id);
  }
  return id;
}

export function getFollowedIds() {
  try {
    const raw = safeGet(K_IDS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function isFollowing(truckId) {
  return getFollowedIds().includes(truckId);
}

function setFollowedIds(ids) {
  safeSet(K_IDS, JSON.stringify(Array.from(new Set(ids))));
}

export function getEmail() {
  return safeGet(K_EMAIL) || null;
}

export function hasEmail() {
  return Boolean(getEmail());
}

export function isOptedIn() {
  return safeGet(K_OPT) === "1";
}

export function wasEmailPrompted() {
  return safeGet(K_PROMPTED) != null;
}

export function markEmailPrompted() {
  safeSet(K_PROMPTED, Date.now().toString());
}

/**
 * Migrate the localStorage anonymous-follow list to the user's account
 * after they log in. Called by `auth.jsx` on every successful login.
 *
 * Strategy: re-POST every locally-followed truck_id through the existing
 * follow endpoint. The backend already accepts `follower_id` (anon) but
 * also reads the JWT cookie when present, so the new rows naturally end
 * up linked to the user. Errors are swallowed individually so a single
 * truck failure doesn't drop the rest. The localStorage list itself is
 * preserved so the user keeps their follow filter pre-populated even
 * before the next /followers/me sync.
 */
export async function migrateAnonymousFollowsToAccount() {
  const ids = getFollowedIds();
  if (!ids.length) return;
  const fid = getFollowerId();
  for (const tid of ids) {
    try {
      await api.post(`/follows/${tid}`, { follower_id: fid });
    } catch {
      // ignore — next /followers/me sync will reconcile
    }
  }
}


/**
 * Follow a truck — optimistic: localStorage updates first, then network
 * syncs in the background. Errors are swallowed so the UI never shows
 * a toast for a background sync; the next `/followers/me` call will
 * reconcile state naturally.
 */
export async function followTruck(truckId) {
  const ids = getFollowedIds();
  if (!ids.includes(truckId)) setFollowedIds([...ids, truckId]);
  try {
    await api.post(`/follows/${truckId}`, { follower_id: getFollowerId() });
  } catch {
    /* swallow — local state still reflects user's intent */
  }
}

export async function unfollowTruck(truckId) {
  const ids = getFollowedIds().filter((id) => id !== truckId);
  setFollowedIds(ids);
  try {
    await api.delete(`/follows/${truckId}`, {
      data: { follower_id: getFollowerId() },
    });
  } catch { /* swallow */ }
}

/**
 * Save email + notification preference. Always round-trips to the
 * server so the user will actually receive digests.
 */
export async function saveProfile({ email, optIn }) {
  const body = { follower_id: getFollowerId() };
  if (email != null) body.email = email;
  if (optIn != null) body.notifications_opt_in = optIn;
  const { data } = await api.post("/followers/profile", body);
  if (data?.email) safeSet(K_EMAIL, data.email);
  safeSet(K_OPT, data?.notifications_opt_in ? "1" : "0");
  return data;
}

/**
 * Which prompt (if any) to show immediately after the user clicks follow?
 *   - "email"   → we need an email; show the capture modal.
 *   - "optin"   → we have the email but the user hasn't opted-in yet.
 *   - "none"    → already opted-in — just confirm with a toast.
 *
 * After the 1st follow we still don't prompt (low friction). On the 2nd
 * follow we push the email prompt, then remember it so we don't nag.
 */
export function promptStateAfterFollow() {
  const count = getFollowedIds().length;
  if (!hasEmail()) {
    if (count >= 2 && !wasEmailPrompted()) return "email";
    return "none";
  }
  if (!isOptedIn()) return "optin";
  return "none";
}

/**
 * Fetch the server's view of the follower so we can reconcile localStorage
 * across devices / after a cache clear. Silent on failure.
 */
export async function syncProfileFromServer() {
  try {
    const { data } = await api.get(`/followers/me?follower_id=${encodeURIComponent(getFollowerId())}`);
    if (data?.email) safeSet(K_EMAIL, data.email);
    safeSet(K_OPT, data?.notifications_opt_in ? "1" : "0");
    if (Array.isArray(data?.followed_truck_ids)) {
      setFollowedIds(data.followed_truck_ids);
    }
    return data;
  } catch {
    return null;
  }
}
